import { cn } from "@/common/helpers/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { InputVariant, inputVariants } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import i18next from "i18next";
import { Check, ChevronDown } from "lucide-react";
import { type Ref, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Props for SearchableSelect component
 */
interface SearchableSelectProps {
  /**
   * An array of option objects to be displayed in the select component.
   */
  options: {
    /** The text to display for the option. */
    label: string;
    /** The unique value associated with the option. */
    value: string;
  }[];

  /**
   * Callback function triggered when the selected value changes.
   */
  onValueChange: (value: string) => void;

  /** The currently selected value. */
  value?: string;

  /**
   * Placeholder text to be displayed when no value is selected.
   */
  placeholder?: string;

  /**
   * Text to display when no search results are found.
   */
  emptyText?: string;

  /**
   * Placeholder for the search input.
   */
  searchPlaceholder?: string;

  /**
   * Additional class names for the trigger button.
   */
  className?: string;

  /**
   * The id of the button element.
   */
  id?: string;

  /**
   * Whether the component is disabled.
   */
  disabled?: boolean;

  /**
   * Whether to show a clear option when a value is selected.
   */
  clearable?: boolean;

  /**
   * Text for the clear option.
   */
  clearText?: string;

  /** Input shape variant. */
  variant?: InputVariant;

  /** Ref forwarded to the trigger button. */
  ref?: Ref<HTMLButtonElement>;
}

export const SearchableSelect = ({
  options,
  onValueChange,
  value,
  placeholder = i18next.t("common:Select"),
  emptyText = i18next.t("common:NoOptionsFound"),
  searchPlaceholder = i18next.t("common:Search"),
  className,
  id,
  disabled = false,
  clearable = true,
  clearText = i18next.t("common:ClearSelection"),
  variant,
  ref,
}: SearchableSelectProps) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((option) => option.value === value);

  const handleSelect = (selectedValue: string): void => {
    if (selectedValue === "clear") {
      onValueChange("");
      setOpen(false);
      return;
    }

    onValueChange(selectedValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={ref}
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            inputVariants({ variant }),
            "flex h-9 w-full items-center justify-between px-3 py-2 text-sm",
            "hover:bg-accent hover:text-accent-foreground cursor-pointer",
            !selectedOption && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate">{selectedOption ? selectedOption.label : (placeholder ?? t("Select"))}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder ?? t("Search")} />
          <CommandList>
            <CommandEmpty>{emptyText ?? t("NoResultsFound")}</CommandEmpty>
            <CommandGroup>
              {clearable && value && (
                <CommandItem
                  key="clear"
                  onSelect={() => handleSelect("clear")}
                  className="text-muted-foreground cursor-pointer italic"
                >
                  {clearText ?? t("common:ClearSelection")}
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem key={option.value} onSelect={() => handleSelect(option.value)} className="cursor-pointer">
                  <Check className={cn("mr-2 h-4 w-4", value === option.value ? "opacity-100" : "opacity-0")} />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

SearchableSelect.displayName = "SearchableSelect";
